import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { fetchTeacherManagementOverview } from "../../../utils/teacherHrApi";
import { getSchoolIdForUser } from "../../../utils/userContext";

function StatCard({ icon, label, value, sub, color = "primary" }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2.5,
                borderRadius: 2.5,
                height: "100%",
                borderColor: (t) => alpha(t.palette[color].main, 0.25),
                bgcolor: (t) => alpha(t.palette[color].main, 0.04),
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: (t) => alpha(t.palette[color].main, 0.12),
                        color: `${color}.main`,
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {label}
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                        {value}
                    </Typography>
                    {sub ? (
                        <Typography variant="caption" color="text.secondary">
                            {sub}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>
        </Paper>
    );
}

function tscChip(status) {
    const s = status || "Pending";
    if (s === "Verified") return { label: "TSC verified", color: "success" };
    if (s === "Rejected") return { label: "TSC rejected", color: "error" };
    return { label: "TSC pending", color: "warning" };
}

export default function TeacherManagementOverview() {
    const navigate = useNavigate();
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        setError("");
        try {
            const overview = await fetchTeacherManagementOverview(schoolId);
            setData(overview);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Could not load teacher management overview.");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const pendingActions = useMemo(() => {
        if (!data) return 0;
        return (
            (data.pendingQualificationVerifications || 0) +
            (data.pendingLeaveApplications || 0) +
            (data.tscPending || 0)
        );
    }, [data]);

    if (loading && !data) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 32%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Human resources
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                            Teacher management
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mt: 0.5 }}>
                            TSC verification, qualifications, CPD, deployment history, payroll, and appraisals — aligned with
                            Malawi Teachers Service Commission records.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate("/Admin/teachers")}>
                            Staff register
                        </Button>
                        <Button variant="outlined" onClick={() => navigate("/Admin/leaves")}>
                            Leave approvals
                        </Button>
                    </Stack>
                </Stack>

                {error ? (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {data ? (
                    <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<BadgeOutlinedIcon />}
                                    label="Staff on roll"
                                    value={data.staffCount ?? 0}
                                    sub={`${data.profilesIncomplete ?? 0} incomplete profiles`}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<VerifiedUserOutlinedIcon />}
                                    label="TSC verified"
                                    value={data.tscVerified ?? 0}
                                    sub={`${data.tscPending ?? 0} pending verification`}
                                    color="success"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<MenuBookOutlinedIcon />}
                                    label="CPD hours"
                                    value={data.totalCpdHours ?? 0}
                                    sub={`${data.cpdRecordCount ?? 0} records logged`}
                                    color="info"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<EventAvailableOutlinedIcon />}
                                    label="Pending actions"
                                    value={pendingActions}
                                    sub={`${data.pendingQualificationVerifications ?? 0} quals · ${data.pendingLeaveApplications ?? 0} leave`}
                                    color="warning"
                                />
                            </Grid>
                        </Grid>

                        {(data.pendingQualificationVerifications > 0 || data.pendingLeaveApplications > 0) && (
                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                {data.pendingQualificationVerifications > 0
                                    ? `${data.pendingQualificationVerifications} qualification(s) awaiting verification. `
                                    : ""}
                                {data.pendingLeaveApplications > 0
                                    ? `${data.pendingLeaveApplications} leave application(s) pending approval. `
                                    : ""}
                                Open a teacher profile → <strong>HR dossier</strong> to verify documents, or go to{" "}
                                <Button size="small" onClick={() => navigate("/Admin/leaves")}>
                                    Leave approvals
                                </Button>
                            </Alert>
                        )}

                        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                            <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <SchoolOutlinedIcon color="primary" />
                                    <Typography fontWeight={700}>Staff HR snapshot</Typography>
                                </Stack>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>TCM no.</TableCell>
                                            <TableCell>TSC status</TableCell>
                                            <TableCell>Contract</TableCell>
                                            <TableCell>Leave</TableCell>
                                            <TableCell>Salary</TableCell>
                                            <TableCell align="right">Profile</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.teachers || []).map((t) => {
                                            const chip = tscChip(t.tscVerificationStatus);
                                            const id = t._id || t.id;
                                            return (
                                                <TableRow key={id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/Admin/teachers/teacher/${id}`)}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                                                    <TableCell>{t.role || "Teacher"}</TableCell>
                                                    <TableCell>{t.tscNumber || "—"}</TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>{t.contractType || "—"}</TableCell>
                                                    <TableCell>
                                                        {t.annualLeaveUsed ?? 0} / {t.annualLeaveAllocated ?? "—"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={t.salaryPaymentStatus || "Unpaid"}
                                                            color={t.salaryPaymentStatus === "Paid" ? "success" : "default"}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Chip
                                                            size="small"
                                                            label={t.profileCompleted ? "Complete" : "Incomplete"}
                                                            color={t.profileCompleted ? "success" : "warning"}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                ) : null}
            </Container>
        </Box>
    );
}
